import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { ExperienceSettingsContent } from '@/views/participant-experience/components';

export interface ParticipantExperienceSettingsModalProps {
  organizationId: string;
  study?: string | null;
  onClose: () => void;
}

export function ParticipantExperienceSettingsModal({
  organizationId,
  study,
  onClose,
}: ParticipantExperienceSettingsModalProps) {
  const dialog = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const requestClose = () => {
    if (!busy && (!dirty || window.confirm('Discard unsaved participant experience changes?')))
      onClose();
  };
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/40 p-2 sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="participant-experience-title"
        tabIndex={-1}
        className="flex max-h-[95vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            requestClose();
          }
          if (event.key === 'Tab') {
            const elements = Array.from(
              dialog.current?.querySelectorAll<HTMLElement>(
                'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]'
              ) ?? []
            ).filter((element) => !element.closest('fieldset:disabled'));
            const first = elements[0];
            const last = elements[elements.length - 1];
            if (!first) {
              event.preventDefault();
              return;
            }
            if (
              event.shiftKey &&
              (document.activeElement === first || document.activeElement === dialog.current)
            ) {
              event.preventDefault();
              last?.focus();
            } else if (
              !event.shiftKey &&
              (document.activeElement === last || document.activeElement === dialog.current)
            ) {
              event.preventDefault();
              first.focus();
            }
          }
        }}
      >
        <div className="flex items-center justify-between gap-4 border-b border-neutral-200 px-5 py-4">
          <h2 id="participant-experience-title" className="text-title font-semibold">
            Participant experience profiles
          </h2>
          <button
            type="button"
            disabled={busy}
            onClick={requestClose}
            aria-label="Close participant experience settings"
            className="focus-ring rounded-lg p-2 hover:bg-neutral-100 disabled:opacity-50"
          >
            <X className="size-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-4 sm:p-6">
          <ExperienceSettingsContent
            organizationId={organizationId}
            study={study ?? null}
            onDirtyChange={setDirty}
            onBusyChange={setBusy}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
